import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs'
import * as companyService from '../services/company'

// Actions
const TOGGLE_COURIER_SELECTION = 'geolocation/TOGGLE_COURIER_SELECTION'
const TOGGLE_COURIER_EXPANSION = 'geolocation/TOGGLE_COURIER_EXPANSION'
const SET_SATELLITE_VIEW = 'geolocation/SET_SATELLITE_VIEW'
const FETCH_COURIERS_REQUEST = 'geolocation/FETCH_COURIERS_REQUEST'
const SET_COURIERS = 'geolocation/SET_COURIERS'
const SET_COURIERS_LOADING = 'geolocation/SET_COURIERS_LOADING'
const SET_COURIERS_ERROR = 'geolocation/SET_COURIERS_ERROR'

// Action Creators
export const toggleCourierSelection = (courierId) => ({
  type: TOGGLE_COURIER_SELECTION,
  payload: courierId,
})

export const toggleCourierExpansion = (courierId) => ({
  type: TOGGLE_COURIER_EXPANSION,
  payload: courierId,
})

export const setSatelliteView = (enabled) => ({
  type: SET_SATELLITE_VIEW,
  payload: enabled,
})

export const fetchCouriersRequest = (companyId) => ({
  type: FETCH_COURIERS_REQUEST,
  payload: companyId,
})

export const setCouriers = (couriers) => ({
  type: SET_COURIERS,
  payload: couriers,
})

export const setCouriersLoading = (loading) => ({
  type: SET_COURIERS_LOADING,
  payload: loading,
})

export const setCouriersError = (error) => ({
  type: SET_COURIERS_ERROR,
  payload: error,
})

// Function to get address from coordinates using Google Maps Geocoding API
const getAddressFromCoordinates = async (latitude, longitude) => {
  try {
    const apiKey = process.env.REACT_APP_GMAPS_KEY
    if (!apiKey) {
      console.warn('Google Maps API key not found')
      return ''
    }

    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}&language=fr`,
    )

    const data = await response.json()

    if (data.status === 'OK' && data.results.length > 0) {
      // Return the formatted address of the first result
      return data.results[0].formatted_address
    }

    return ''
  } catch (error) {
    console.error('Error getting address from coordinates:', error)
    return ''
  }
}

// Epic to handle fetching couriers
export function fetchCouriersEpic(action$) {
  return action$.ofType(FETCH_COURIERS_REQUEST).mergeMap((action) => {
    const companyId = action.payload

    return Observable.concat(
      Observable.of(setCouriersLoading(true), setCouriersError(null)),
      Observable.ajax(companyService.getCompany({ id: companyId }))
        .mergeMap(async (response) => {
          // Extract couriers from company response
          const couriers = response.response.couriers || []

          // Transform API data to match expected format with address resolution
          const transformedCouriers = await Promise.all(
            couriers.map(async (courier) => {
              const positionHistory = courier.positionHistory || []
              const latestPosition = positionHistory[0]

              // Transform position history to expected coordinate format
              const transformedHistory = positionHistory.map((position) => ({
                timestamp: position.timestamp,
                coordinates: [position.latitude, position.longitude],
              }))

              // Get current address from coordinates if not provided
              let currentAddress = latestPosition?.address || ''
              if (!currentAddress && latestPosition?.latitude && latestPosition?.longitude) {
                currentAddress = await getAddressFromCoordinates(latestPosition.latitude, latestPosition.longitude)
              }

              return {
                id: courier.id,
                firstName: courier.firstname,
                lastName: courier.lastname,
                lastLocation: latestPosition?.timestamp || null,
                currentCoordinates:
                  latestPosition?.latitude && latestPosition?.longitude
                    ? [latestPosition.latitude, latestPosition.longitude]
                    : null,
                currentAddress: currentAddress,
                history: transformedHistory,
                disabled: !courier.geolocationEnabled,
              }
            }),
          )

          return setCouriers(transformedCouriers)
        })
        .catch((error) => Observable.of(setCouriersError(error.message || 'Failed to fetch couriers')))
        .concat(Observable.of(setCouriersLoading(false))),
    )
  })
}

// Initial State
const initialState = {
  selectedCourier: null,
  expandedCouriers: {},
  satelliteView: false,
  couriers: [],
  couriersLoading: false,
  couriersError: null,
}

export const epic = combineEpics(fetchCouriersEpic)

// Reducer
export function reducer(state = initialState, action) {
  switch (action.type) {
    case TOGGLE_COURIER_SELECTION:
      return {
        ...state,
        selectedCourier: state.selectedCourier === action.payload ? null : action.payload,
      }

    case TOGGLE_COURIER_EXPANSION:
      return {
        ...state,
        expandedCouriers: {
          ...state.expandedCouriers,
          [action.payload]: !state.expandedCouriers[action.payload],
        },
      }

    case SET_SATELLITE_VIEW:
      return {
        ...state,
        satelliteView: action.payload,
      }

    case SET_COURIERS:
      return {
        ...state,
        couriers: action.payload,
        couriersError: null,
      }

    case SET_COURIERS_LOADING:
      return {
        ...state,
        couriersLoading: action.payload,
      }

    case SET_COURIERS_ERROR:
      return {
        ...state,
        couriersError: action.payload,
        couriersLoading: false,
      }

    default:
      return state
  }
}
